/**
 * Radiomics popover — v1.1 (dibujo + extracción in-app + params override
 * + extracción desde clase anotada).
 *
 * ROIs efímeras: las polilíneas se dibujan en sesión, viajan al backend en
 * el POST y NO se persisten en `Annotation`. Lo único que queda es el JSON
 * de features en disco.
 *
 * También se puede extraer "desde clase anotada": el usuario elige una
 * clase del dropdown (Liver, Aorta, ...) y la API recibe las polilíneas
 * que ese radiólogo ya dibujó como parte del workflow anatómico.
 *
 * Los parámetros pyRadiomics son configurables vía un panel "Avanzado".
 * Sólo los modificados respecto al YAML del backend se envían como
 * `params_override`. Se pueden guardar perfiles en `localStorage` del
 * navegador.
 */

import { showAlert } from "@/helpers/alerts";

const API_BASE = () => `${process.env.VUE_APP_API_URL}/test`;

// Familias de features para la categorización en el panel de resultados.
const FEATURE_FAMILIES = [
    { key: "shape",      label: "Shape" },
    { key: "firstorder", label: "First Order" },
    { key: "glcm",       label: "GLCM" },
    { key: "glrlm",      label: "GLRLM" },
    { key: "glszm",      label: "GLSZM" },
    { key: "gldm",       label: "GLDM" },
    { key: "ngtdm",      label: "NGTDM" },
];

// Conteo de features por clase IBSI (para el estimador en vivo del panel).
const CLASS_FEATURE_COUNTS = {
    shape: 16, firstorder: 19, glcm: 24, glrlm: 16,
    glszm: 16, gldm: 14, ngtdm: 5,
};

// Defaults coherentes con `tools/radiomics_params.yaml`. Sólo los valores
// que el usuario cambie respecto a esto se envían como `params_override`
// — el resto se aplica desde el YAML del servidor.
function _defaultParams() {
    return {
        discretization: { mode: "binWidth", binWidth: 25, binCount: 32 },
        resampling: {
            pixelSpacing: [1, 1, 1],
            interpolator: "sitkBSpline",
            padDistance: 5,
            preCrop: true,
        },
        ct: { voxelArrayShift: 1000 },
        reseg: { lower: -100, upper: 400, mode: "absolute", shape: false },
        intensity: {
            normalize: false,
            normalizeScale: 1,
            removeOutliers: "",  // vacío = sin filtro
        },
        maskVal: { minDims: 2, minSize: 10, correctMask: false },
        featureClasses: {
            shape: true, firstorder: true, glcm: true, glrlm: true,
            glszm: true, gldm: true, ngtdm: true,
        },
        imageTypes: {
            Original:    { enabled: true },
            LoG:         { enabled: false, sigmas: "1.0, 3.0, 5.0" },
            Wavelet:     { enabled: false, wavelet: "coif1" },
            Square:      { enabled: false },
            SquareRoot:  { enabled: false },
            Logarithm:   { enabled: false },
            Exponential: { enabled: false },
            Gradient:    { enabled: false },
        },
        glcm: {
            distances: "1",
            weightingNorm: "none",
            symmetricalGLCM: true,
            gldm_a: 0,
        },
        force2D: { enabled: false, dimension: 0 },
    };
}


// ============================== Estado ==============================

const extractionCache = new Map();

let drawingState = null;
const pendingRadiomicUIDs = new Set();

let _viewerCtx = null;
let _listenerInstalled = false;
let _activeRmvCode = null;

let paramsState = _defaultParams();
let currentProfileName = "Default";

// ============================ Profiles ============================

const PROFILE_STORAGE_KEY = "removirt_radiomics_profiles";

function _loadProfiles() {
    try {
        return JSON.parse(localStorage.getItem(PROFILE_STORAGE_KEY) || "{}");
    } catch {
        return {};
    }
}

function _saveProfiles(profiles) {
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profiles));
}

function _applyProfile(name) {
    if (name === "Default" || !name) {
        paramsState = _defaultParams();
        currentProfileName = "Default";
        return;
    }
    const profiles = _loadProfiles();
    if (profiles[name]) {
        paramsState = JSON.parse(JSON.stringify(profiles[name]));
        currentProfileName = name;
    }
}

function _persistCurrentAsProfile(name) {
    if (!name || name === "Default") {
        showAlert("error", "Elige un nombre distinto de 'Default'.");
        return false;
    }
    if (!/^[A-Za-z0-9_ -]{1,32}$/.test(name)) {
        showAlert("error", "Nombre de perfil inválido (1-32, [A-Za-z0-9_ -]).");
        return false;
    }
    const profiles = _loadProfiles();
    profiles[name] = JSON.parse(JSON.stringify(paramsState));
    _saveProfiles(profiles);
    currentProfileName = name;
    return true;
}

function _deleteProfile(name) {
    if (name === "Default") return;
    const profiles = _loadProfiles();
    delete profiles[name];
    _saveProfiles(profiles);
    if (currentProfileName === name) {
        _applyProfile("Default");
    }
}


// ===================== Construcción del override =====================

/**
 * Convierte `paramsState` al dict {imageType, featureClass, setting} que
 * espera el backend. Incluye TODO el estado actual; el backend mergea con su
 * YAML. Mantenemos la simplicidad de "envío completo" en vez de diff porque
 * el JSON resultante es pequeño y deja totalmente trazado qué se usó.
 */
function _buildParamsOverride() {
    const setting = {};

    // Discretización
    if (paramsState.discretization.mode === "binWidth") {
        setting.binWidth = _num(paramsState.discretization.binWidth);
    } else {
        setting.binCount = _int(paramsState.discretization.binCount);
    }

    // Resampling
    setting.resampledPixelSpacing = paramsState.resampling.pixelSpacing.map(_num);
    setting.interpolator = paramsState.resampling.interpolator;
    setting.padDistance = _int(paramsState.resampling.padDistance);
    setting.preCrop = !!paramsState.resampling.preCrop;

    // CT-specific
    setting.voxelArrayShift = _num(paramsState.ct.voxelArrayShift);

    // Resegmentación
    if (paramsState.reseg.lower !== "" && paramsState.reseg.upper !== "") {
        setting.resegmentRange = [_num(paramsState.reseg.lower), _num(paramsState.reseg.upper)];
        setting.resegmentMode = paramsState.reseg.mode;
        setting.resegmentShape = !!paramsState.reseg.shape;
    }

    // Intensidad
    setting.normalize = !!paramsState.intensity.normalize;
    setting.normalizeScale = _num(paramsState.intensity.normalizeScale);
    const ro = paramsState.intensity.removeOutliers;
    if (ro !== "" && ro !== null && ro !== undefined) {
        setting.removeOutliers = _num(ro);
    }

    // Mask validation
    setting.minimumROIDimensions = _int(paramsState.maskVal.minDims);
    setting.minimumROISize = _int(paramsState.maskVal.minSize);
    setting.correctMask = !!paramsState.maskVal.correctMask;

    // GLCM/textura
    setting.distances = paramsState.glcm.distances
        .split(",").map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
    if (setting.distances.length === 0) setting.distances = [1];
    if (paramsState.glcm.weightingNorm !== "none") {
        setting.weightingNorm = paramsState.glcm.weightingNorm;
    }
    setting.symmetricalGLCM = !!paramsState.glcm.symmetricalGLCM;
    setting.gldm_a = _num(paramsState.glcm.gldm_a);

    // 2D
    if (paramsState.force2D.enabled) {
        setting.force2D = true;
        setting.force2Ddimension = _int(paramsState.force2D.dimension);
    }

    // Always include label=1 (el backend rasteriza máscaras binarias)
    setting.label = 1;

    // Feature classes
    const featureClass = {};
    for (const [k, v] of Object.entries(paramsState.featureClasses)) {
        if (v) featureClass[k] = null;  // null = todas las features de la clase
    }

    // Image types
    const imageType = {};
    for (const [k, cfg] of Object.entries(paramsState.imageTypes)) {
        if (!cfg.enabled) continue;
        if (k === "LoG") {
            const sigmas = cfg.sigmas.split(",")
                .map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
            imageType[k] = { sigma: sigmas.length ? sigmas : [1.0, 3.0, 5.0] };
        } else if (k === "Wavelet") {
            imageType[k] = { wavelet: cfg.wavelet || "coif1" };
        } else {
            imageType[k] = {};
        }
    }

    return { imageType, featureClass, setting };
}

function _num(v) { const n = Number(v); return isNaN(n) ? 0 : n; }
function _int(v) { const n = parseInt(v, 10); return isNaN(n) ? 0 : n; }


/**
 * Estimador rápido del número de features que va a producir la config actual.
 * Shape se calcula sólo una vez (independiente de filtros); el resto se
 * multiplica por el número de filtros activos.
 */
function _estimateFeatureCount() {
    const enabledClasses = Object.entries(paramsState.featureClasses)
        .filter(([, v]) => v).map(([k]) => k);

    let nFilters = 0;
    for (const [k, cfg] of Object.entries(paramsState.imageTypes)) {
        if (!cfg.enabled) continue;
        if (k === "LoG") {
            const n = cfg.sigmas.split(",").map(s => s.trim()).filter(Boolean).length;
            nFilters += (n || 1);
        } else if (k === "Wavelet") {
            nFilters += 8;  // 8 sub-bandas por nivel
        } else {
            nFilters += 1;
        }
    }
    if (nFilters === 0) nFilters = 1;

    let total = 0;
    for (const c of enabledClasses) {
        if (c === "shape") total += CLASS_FEATURE_COUNTS[c];
        else total += CLASS_FEATURE_COUNTS[c] * nFilters;
    }
    return total;
}


// =========================== Predicado público ===========================

export function isRadiomicAnnotation(annotationUID) {
    return pendingRadiomicUIDs.has(annotationUID);
}


// =========================== Setup principal ===========================

/**
 * @param {string} rmvCode
 * @param {string} token
 * @param {object} viewerCtx — handles Cornerstone necesarios:
 *   - cornerstoneTools, eventTarget, csToolsEnums
 *   - ToolGroupManager, toolGroupId, planarFreehandToolName
 *   - annotationGroups: Array<AnnotationGroup>
 *   - segmentMapJsonLoaded: Map<displayName, { id, color? }>
 */
export function setupRadiomicsPopover(rmvCode, token, viewerCtx) {
    const button = document.getElementById("show_radiomics");
    const popover = document.getElementById("radiomics-popover");
    if (!button || !popover || !rmvCode || !token) return;

    _viewerCtx = viewerCtx;
    _activeRmvCode = rmvCode;

    popover.innerHTML = _scaffoldHTML();
    _renderConfigSection();
    _renderFromClassSection();
    _renderDrawingSection();

    button.onclick = (e) => {
        e.stopPropagation();
        const willOpen = popover.style.display === "none";
        popover.style.display = willOpen ? "block" : "none";
        if (willOpen) {
            extractionCache.clear();
            // Re-render del dropdown "Desde clase anotada": las anotaciones
            // pueden haberse cargado/editado después del setup inicial.
            _renderFromClassSection();
            _refreshList(rmvCode);
        }
    };

    // No cerrar si se está dibujando (clicks en el viewport para añadir
    // puntos no deben colapsar el panel).
    document.addEventListener("click", (e) => {
        if (popover.style.display === "none") return;
        if (popover.contains(e.target)) return;
        if (button.contains(e.target)) return;
        if (drawingState) return;
        popover.style.display = "none";
    });

    popover.addEventListener("click", (e) => {
        const action = e.target.closest("[data-action]");
        if (!action) return;
        e.stopPropagation();
        const kind = action.dataset.action;
        const roiName = action.dataset.roi;

        if (kind === "refresh")            { extractionCache.clear(); _renderFromClassSection(); _refreshList(rmvCode); }
        else if (kind === "view")          { _toggleFeatures(rmvCode, roiName); }
        else if (kind === "csv")           { _downloadCsv(rmvCode, roiName); }
        else if (kind === "delete")        { _deleteExtraction(rmvCode, roiName); }
        else if (kind === "start_drawing") {
            const input = popover.querySelector("#radiomics-new-name");
            _startDrawing((input?.value || "").trim());
        }
        else if (kind === "cancel_drawing")  { _cancelDrawing(); }
        else if (kind === "extract_pending") { _extractPending(rmvCode); }
        else if (kind === "extract_from_class") {
            const cls = popover.querySelector("#radiomics-fromclass-select")?.value;
            const name = (popover.querySelector("#radiomics-fromclass-name")?.value || "").trim();
            _extractFromClass(rmvCode, cls, name);
        }
        else if (kind === "toggle_config")   { _toggleConfigOpen(); }
        else if (kind === "toggle_advanced") { _toggleAdvancedOpen(); }
        else if (kind === "save_profile") {
            const name = (popover.querySelector("#radiomics-new-profile")?.value || "").trim();
            if (_persistCurrentAsProfile(name)) {
                showAlert("success", `Perfil '${name}' guardado.`);
                _renderConfigSection();
            }
        }
        else if (kind === "delete_profile") {
            const sel = popover.querySelector("#radiomics-profile-select");
            const name = sel?.value;
            if (name && name !== "Default" && confirm(`¿Borrar el perfil '${name}'?`)) {
                _deleteProfile(name);
                _renderConfigSection();
            }
        }
        else if (kind === "reset_defaults") {
            _applyProfile("Default");
            _renderConfigSection();
        }
    });

    // Cambios en inputs del panel de config / from-class.
    popover.addEventListener("change", _handleConfigChange);
    popover.addEventListener("input",  _handleConfigChange);

    _installAnnotationListener();
}


// =========================== Handlers de config ===========================

let _configOpen = false;
let _advancedOpen = false;

function _toggleConfigOpen() {
    _configOpen = !_configOpen;
    _renderConfigSection();
}
function _toggleAdvancedOpen() {
    _advancedOpen = !_advancedOpen;
    _renderConfigSection();
}

function _handleConfigChange(e) {
    const t = e.target;
    if (!t || !t.dataset) return;
    const path = t.dataset.paramPath;
    if (!path) return;

    let value;
    if (t.type === "checkbox") value = t.checked;
    else if (t.type === "number") value = t.value === "" ? "" : Number(t.value);
    else value = t.value;

    _setByPath(paramsState, path, value);

    // Perfil dropdown cambia — aplicar el perfil seleccionado.
    if (path === "_profile") {
        _applyProfile(value);
        _renderConfigSection();
        return;
    }

    // Inputs que afectan al estimador de features → re-render parcial.
    if (path.startsWith("featureClasses.") || path.startsWith("imageTypes.") ||
        path === "discretization.mode") {
        _renderConfigSection();
    } else {
        // Sólo refrescar el contador.
        const counter = document.getElementById("radiomics-feature-count");
        if (counter) counter.textContent = `~${_estimateFeatureCount()}`;
    }
}

function _setByPath(obj, path, value) {
    const parts = path.split(".");
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        const idx = k.match(/^(.+)\[(\d+)\]$/);
        if (idx) {
            cur = cur[idx[1]];
            cur = cur[Number(idx[2])];
        } else {
            cur = cur[k];
        }
    }
    const last = parts[parts.length - 1];
    const idx = last.match(/^(.+)\[(\d+)\]$/);
    if (idx) {
        cur[idx[1]][Number(idx[2])] = value;
    } else {
        cur[last] = value;
    }
}


// =========================== HTTP helpers ===========================

async function _fetchJson(url, options = {}) {
    const response = await fetch(url, {
        ...options,
        headers: {
            "Authorization": `Bearer ${token_in_window()}`,
            ...(options.headers || {}),
        },
    });
    if (response.status === 401) {
        window.location.replace(`${process.env.VUE_APP_WEBAPP_URL}/account/login`);
        throw new Error("Unauthorized");
    }
    if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
    }
    return response.json();
}

function token_in_window() {
    const params = new URLSearchParams(window.location.search);
    return params.get("token");
}


// =========================== Drawing flow ===========================

function _installAnnotationListener() {
    if (_listenerInstalled || !_viewerCtx) return;
    const { eventTarget, csToolsEnums } = _viewerCtx;
    eventTarget.addEventListener(
        csToolsEnums.Events.ANNOTATION_COMPLETED,
        _handleAnnotationCompleted,
    );
    // ANNOTATION_ADDED dispara también cuando `loadAnnotationsFromVersion`
    // mete las polilíneas guardadas (mientras que ANNOTATION_COMPLETED sólo
    // para dibujo manual). Lo usamos para refrescar el contador "Desde clase
    // anotada" sin esperar a que el usuario abra el panel a posteriori.
    // Debounce: la carga inserta cientos de polilíneas en sucesión.
    if (csToolsEnums.Events.ANNOTATION_ADDED) {
        eventTarget.addEventListener(
            csToolsEnums.Events.ANNOTATION_ADDED,
            _scheduleFromClassRefresh,
        );
    }
    _listenerInstalled = true;
}

let _fromClassRefreshTimer = null;
function _scheduleFromClassRefresh() {
    if (_fromClassRefreshTimer) clearTimeout(_fromClassRefreshTimer);
    _fromClassRefreshTimer = setTimeout(() => {
        _fromClassRefreshTimer = null;
        // Sólo re-renderizar si el panel está abierto, para no pegarse contra
        // el DOM cuando el usuario no lo está mirando.
        const popover = document.getElementById("radiomics-popover");
        if (popover && popover.style.display !== "none") {
            _renderFromClassSection();
        }
    }, 200);
}

function _handleAnnotationCompleted(evt) {
    if (!drawingState || drawingState.extracting) return;

    const annotation = evt?.detail?.annotation;
    const uid = annotation?.annotationUID;
    const polyline = annotation?.data?.contour?.polyline;
    if (!uid || !Array.isArray(polyline) || polyline.length < 3) return;

    drawingState.polylines.push(polyline.map(p => [p[0], p[1], p[2]]));
    drawingState.annotationUIDs.add(uid);
    pendingRadiomicUIDs.add(uid);

    // Sacar del AnnotationGroup anatómico al que el handler existente lo añadió.
    if (Array.isArray(_viewerCtx?.annotationGroups)) {
        for (const group of _viewerCtx.annotationGroups) {
            if (!group) continue;
            try {
                if (typeof group.remove === "function" && group.annotationUIDs?.has?.(uid)) {
                    group.remove(uid);
                } else if (group.annotationUIDs?.has?.(uid)) {
                    group.annotationUIDs.delete(uid);
                }
            } catch (err) {
                console.warn("[radiomics] no pude desasignar del grupo anatómico", err);
            }
        }
    }
    _renderDrawingSection();
}

function _startDrawing(name) {
    if (drawingState) {
        showAlert("error", "Ya hay una ROI en dibujo. Termina o cancela primero.");
        return;
    }
    if (!/^[A-Za-z0-9_]{1,32}$/.test(name)) {
        showAlert("error", "Nombre inválido. Sólo [A-Za-z0-9_], 1-32 caracteres.");
        return;
    }
    drawingState = { name, polylines: [], annotationUIDs: new Set(), extracting: false };
    _renderDrawingSection();
    showAlert("success", `Dibujando ROI '${name}'.`);
}

function _cancelDrawing() {
    if (!drawingState) return;
    if (drawingState.extracting) {
        showAlert("error", "La extracción está en curso, espera a que termine.");
        return;
    }
    _purgePendingAnnotations();
    drawingState = null;
    _renderDrawingSection();
}

async function _extractPending(rmvCode) {
    if (!drawingState || drawingState.extracting) return;
    if (drawingState.polylines.length === 0) {
        showAlert("error", "Dibuja al menos una polilínea antes de extraer.");
        return;
    }
    const { name, polylines } = drawingState;
    drawingState.extracting = true;
    _renderDrawingSection();
    try {
        const payload = await _postExtract(rmvCode, name, polylines);
        extractionCache.set(name, payload);
        _purgePendingAnnotations();
        drawingState = null;
        _renderDrawingSection();
        showAlert("success", `ROI '${name}' extraída: ${Object.keys(payload.features || {}).length} features.`);
        await _refreshList(rmvCode);
        setTimeout(() => _toggleFeatures(rmvCode, name), 50);
    } catch (err) {
        if (drawingState) drawingState.extracting = false;
        _renderDrawingSection();
        showAlert("error", `No se pudo extraer: ${err.message}`);
    }
}

function _purgePendingAnnotations() {
    if (!drawingState || !_viewerCtx?.cornerstoneTools) return;
    const cs = _viewerCtx.cornerstoneTools;
    for (const uid of drawingState.annotationUIDs) {
        try { cs.annotation.state.removeAnnotation(uid); }
        catch (err) { console.warn("[radiomics] no pude borrar anotación", uid, err); }
        pendingRadiomicUIDs.delete(uid);
    }
    drawingState.annotationUIDs.clear();
    drawingState.polylines.length = 0;
}


// =========================== Extract desde clase ===========================

function _getPolylinesFromClass(displayName) {
    const map = _viewerCtx?.segmentMapJsonLoaded;
    const groups = _viewerCtx?.annotationGroups;
    if (!map || !groups) return [];
    const info = map.get(displayName);
    if (!info) return [];
    const group = groups[info.id - 1];
    if (!group?.annotationUIDs) return [];

    const cs = _viewerCtx.cornerstoneTools;
    const polys = [];
    for (const uid of group.annotationUIDs) {
        try {
            const ann = cs.annotation.state.getAnnotation(uid);
            const poly = ann?.data?.contour?.polyline;
            if (Array.isArray(poly) && poly.length >= 3) {
                polys.push(poly.map(p => [p[0], p[1], p[2]]));
            }
        } catch (err) {
            console.warn("[radiomics] no pude leer polilínea", uid, err);
        }
    }
    return polys;
}

async function _extractFromClass(rmvCode, className, roiName) {
    if (!className) {
        showAlert("error", "Elige una clase del dropdown.");
        return;
    }
    if (!/^[A-Za-z0-9_]{1,32}$/.test(roiName)) {
        showAlert("error", "Nombre de ROI inválido. Sólo [A-Za-z0-9_], 1-32 caracteres.");
        return;
    }
    const polylines = _getPolylinesFromClass(className);
    if (polylines.length === 0) {
        showAlert("error", `La clase '${className}' no tiene polilíneas dibujadas.`);
        return;
    }

    const btn = document.querySelector('[data-action="extract_from_class"]');
    if (btn) { btn.disabled = true; btn.textContent = "Extrayendo…"; }

    try {
        const payload = await _postExtract(rmvCode, roiName, polylines);
        extractionCache.set(roiName, payload);
        showAlert("success", `ROI '${roiName}' (desde clase ${className}) extraída: ${Object.keys(payload.features || {}).length} features.`);
        await _refreshList(rmvCode);
        setTimeout(() => _toggleFeatures(rmvCode, roiName), 50);
    } catch (err) {
        showAlert("error", `No se pudo extraer: ${err.message}`);
    } finally {
        if (btn) { btn.disabled = false; btn.textContent = "Extraer ya"; }
    }
}

async function _postExtract(rmvCode, roiName, polylines) {
    const body = {
        polylines,
        params_override: _buildParamsOverride(),
    };
    return _fetchJson(`${API_BASE()}/${rmvCode}/radiomics/${roiName}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
}


// =========================== Acciones del listado ===========================

async function _refreshList(rmvCode) {
    const listContainer = document.getElementById("radiomics-list");
    if (!listContainer) return;
    listContainer.innerHTML = `<div class="radiomics-loading">Cargando…</div>`;
    try {
        const data = await _fetchJson(`${API_BASE()}/${rmvCode}/radiomics`);
        listContainer.innerHTML = _listHTML(data.rois || []);
    } catch (err) {
        listContainer.innerHTML = `<div class="radiomics-error">Error: ${_escape(err.message)}</div>`;
    }
}

async function _toggleFeatures(rmvCode, roiName) {
    const detail = document.getElementById(`radiomics-detail-${_slug(roiName)}`);
    if (!detail) return;
    if (detail.dataset.open === "true") {
        detail.innerHTML = ""; detail.dataset.open = "false"; return;
    }
    detail.innerHTML = `<div class="radiomics-loading">Cargando features…</div>`;
    detail.dataset.open = "true";
    try {
        let payload = extractionCache.get(roiName);
        if (!payload) {
            payload = await _fetchJson(`${API_BASE()}/${rmvCode}/radiomics/${roiName}`);
            extractionCache.set(roiName, payload);
        }
        detail.innerHTML = _featuresHTML(payload);
    } catch (err) {
        detail.innerHTML = `<div class="radiomics-error">Error: ${_escape(err.message)}</div>`;
    }
}

async function _downloadCsv(rmvCode, roiName) {
    const url = `${API_BASE()}/${rmvCode}/radiomics/${roiName}/csv`;
    try {
        const response = await fetch(url, {
            headers: { "Authorization": `Bearer ${token_in_window()}` },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `${rmvCode}_${roiName}_radiomics.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
    } catch (err) {
        showAlert("error", `No se pudo descargar el CSV: ${err.message}`);
    }
}

async function _deleteExtraction(rmvCode, roiName) {
    if (!confirm(`¿Borrar la extracción radiómica '${roiName}'?`)) return;
    try {
        await _fetchJson(`${API_BASE()}/${rmvCode}/radiomics/${roiName}`, { method: "DELETE" });
        extractionCache.delete(roiName);
        showAlert("success", `ROI '${roiName}' borrada`);
        _refreshList(rmvCode);
    } catch (err) {
        showAlert("error", `No se pudo borrar: ${err.message}`);
    }
}


// =========================== HTML rendering ===========================

function _scaffoldHTML() {
    return `
        <div class="radiomics-header">
            <h3 class="radiomics-title">Radiomics</h3>
            <button class="radiomics-refresh" data-action="refresh" title="Recargar">⟳</button>
        </div>
        <div id="radiomics-config" class="radiomics-config"></div>
        <div id="radiomics-fromclass" class="radiomics-fromclass"></div>
        <div id="radiomics-drawing" class="radiomics-drawing"></div>
        <div id="radiomics-list" class="radiomics-list"></div>
        <div class="radiomics-footer">
            ROIs efímeras. Polilíneas no persistidas. Sólo features.
        </div>
    `;
}

function _renderConfigSection() {
    const container = document.getElementById("radiomics-config");
    if (!container) return;

    const profiles = _loadProfiles();
    const profileOptions = ["Default", ...Object.keys(profiles)]
        .map(n => `<option value="${_escape(n)}" ${n === currentProfileName ? "selected" : ""}>${_escape(n)}</option>`)
        .join("");

    const featureCount = _estimateFeatureCount();

    if (!_configOpen) {
        container.innerHTML = `
            <button class="radiomics-config-toggle" data-action="toggle_config">
                ⚙ Configuración (perfil: <b>${_escape(currentProfileName)}</b>, ~${featureCount} features) ▸
            </button>
        `;
        return;
    }

    container.innerHTML = `
        <div class="radiomics-config-open">
            <div class="radiomics-config-header">
                <button class="radiomics-config-toggle" data-action="toggle_config">
                    ⚙ Configuración ▾
                </button>
                <span class="radiomics-feature-count">
                    ~<b id="radiomics-feature-count">${featureCount}</b> features
                </span>
            </div>

            <div class="radiomics-profile-row">
                <label>Perfil:</label>
                <select id="radiomics-profile-select" data-param-path="_profile">
                    ${profileOptions}
                </select>
                <button data-action="delete_profile" title="Borrar perfil seleccionado">🗑</button>
                <button data-action="reset_defaults" title="Resetear a defaults">↻</button>
            </div>
            <div class="radiomics-profile-row">
                <input type="text" id="radiomics-new-profile" placeholder="Nombre nuevo perfil" maxlength="32">
                <button data-action="save_profile">💾 Guardar como</button>
            </div>

            ${_groupHTML("Discretización", `
                <div class="radiomics-row2">
                    <label><input type="radio" name="discMode" value="binWidth"
                        ${paramsState.discretization.mode === "binWidth" ? "checked" : ""}
                        data-param-path="discretization.mode"> binWidth</label>
                    <input type="number" step="0.1" min="0.1"
                        data-param-path="discretization.binWidth"
                        value="${paramsState.discretization.binWidth}"
                        ${paramsState.discretization.mode === "binWidth" ? "" : "disabled"}>
                </div>
                <div class="radiomics-row2">
                    <label><input type="radio" name="discMode" value="binCount"
                        ${paramsState.discretization.mode === "binCount" ? "checked" : ""}
                        data-param-path="discretization.mode"> binCount</label>
                    <input type="number" step="1" min="1"
                        data-param-path="discretization.binCount"
                        value="${paramsState.discretization.binCount}"
                        ${paramsState.discretization.mode === "binCount" ? "" : "disabled"}>
                </div>
            `)}

            ${_groupHTML("Resampling", `
                <div class="radiomics-row3">
                    <label>Spacing (mm):</label>
                    <input type="number" step="0.1" min="0" data-param-path="resampling.pixelSpacing[0]" value="${paramsState.resampling.pixelSpacing[0]}">
                    <input type="number" step="0.1" min="0" data-param-path="resampling.pixelSpacing[1]" value="${paramsState.resampling.pixelSpacing[1]}">
                    <input type="number" step="0.1" min="0" data-param-path="resampling.pixelSpacing[2]" value="${paramsState.resampling.pixelSpacing[2]}">
                </div>
            `)}

            ${_groupHTML("CT-specific", `
                <div class="radiomics-row2">
                    <label>voxelArrayShift:</label>
                    <input type="number" step="1" data-param-path="ct.voxelArrayShift" value="${paramsState.ct.voxelArrayShift}">
                </div>
            `)}

            ${_groupHTML("Resegmentación HU", `
                <div class="radiomics-row3">
                    <label>Rango:</label>
                    <input type="number" step="1" data-param-path="reseg.lower" value="${paramsState.reseg.lower}">
                    <input type="number" step="1" data-param-path="reseg.upper" value="${paramsState.reseg.upper}">
                </div>
                <div class="radiomics-row2">
                    <label>Modo:</label>
                    <select data-param-path="reseg.mode">
                        <option value="absolute" ${paramsState.reseg.mode === "absolute" ? "selected" : ""}>absolute</option>
                        <option value="relative" ${paramsState.reseg.mode === "relative" ? "selected" : ""}>relative</option>
                        <option value="sigma" ${paramsState.reseg.mode === "sigma" ? "selected" : ""}>sigma</option>
                    </select>
                </div>
                <div class="radiomics-row2">
                    <label><input type="checkbox" data-param-path="reseg.shape" ${paramsState.reseg.shape ? "checked" : ""}> Usar para shape</label>
                </div>
            `)}

            ${_groupHTML("Feature classes", `
                <div class="radiomics-checkgrid">
                    ${Object.keys(paramsState.featureClasses).map(k => `
                        <label><input type="checkbox" data-param-path="featureClasses.${k}"
                            ${paramsState.featureClasses[k] ? "checked" : ""}> ${k} (${CLASS_FEATURE_COUNTS[k]})</label>
                    `).join("")}
                </div>
            `)}

            ${_groupHTML("Image types (filtros)", `
                <div class="radiomics-checkgrid">
                    ${Object.keys(paramsState.imageTypes).map(k => {
                        const cfg = paramsState.imageTypes[k];
                        let extra = "";
                        if (k === "LoG" && cfg.enabled) {
                            extra = `<input type="text" placeholder="sigmas, ej: 1.0, 3.0, 5.0"
                                data-param-path="imageTypes.LoG.sigmas" value="${_escape(cfg.sigmas)}">`;
                        }
                        if (k === "Wavelet" && cfg.enabled) {
                            extra = `<select data-param-path="imageTypes.Wavelet.wavelet">
                                ${["coif1","haar","db1","db2","db3","sym2","sym3","bior1.1","bior2.2"]
                                    .map(w => `<option value="${w}" ${cfg.wavelet === w ? "selected" : ""}>${w}</option>`).join("")}
                            </select>`;
                        }
                        return `
                            <div class="radiomics-imagetype">
                                <label><input type="checkbox" data-param-path="imageTypes.${k}.enabled"
                                    ${cfg.enabled ? "checked" : ""}> ${k}</label>
                                ${extra}
                            </div>
                        `;
                    }).join("")}
                </div>
            `)}

            <button class="radiomics-config-toggle radiomics-advanced-toggle" data-action="toggle_advanced">
                ${_advancedOpen ? "▾" : "▸"} Avanzado
            </button>

            ${_advancedOpen ? _advancedHTML() : ""}
        </div>
    `;

    // Sync radio button name behavior (sólo uno seleccionado).
    container.querySelectorAll('input[name="discMode"]').forEach(r => {
        r.addEventListener("change", () => {
            paramsState.discretization.mode = r.value;
            _renderConfigSection();
        });
    });
}

function _advancedHTML() {
    return `
        ${_groupHTML("Intensity preprocessing", `
            <div class="radiomics-row2">
                <label><input type="checkbox" data-param-path="intensity.normalize" ${paramsState.intensity.normalize ? "checked" : ""}> Normalize (z-score)</label>
            </div>
            <div class="radiomics-row2">
                <label>Scale:</label>
                <input type="number" step="0.1" data-param-path="intensity.normalizeScale" value="${paramsState.intensity.normalizeScale}">
            </div>
            <div class="radiomics-row2">
                <label>Remove outliers (σ):</label>
                <input type="number" step="0.5" min="0" placeholder="(vacío = sin filtro)" data-param-path="intensity.removeOutliers" value="${paramsState.intensity.removeOutliers}">
            </div>
        `)}

        ${_groupHTML("Resampling avanzado", `
            <div class="radiomics-row2">
                <label>Interpolator:</label>
                <select data-param-path="resampling.interpolator">
                    ${["sitkBSpline","sitkLinear","sitkNearestNeighbor","sitkGaussian"]
                        .map(i => `<option value="${i}" ${paramsState.resampling.interpolator === i ? "selected" : ""}>${i}</option>`).join("")}
                </select>
            </div>
            <div class="radiomics-row2">
                <label>padDistance:</label>
                <input type="number" step="1" min="0" data-param-path="resampling.padDistance" value="${paramsState.resampling.padDistance}">
            </div>
            <div class="radiomics-row2">
                <label><input type="checkbox" data-param-path="resampling.preCrop" ${paramsState.resampling.preCrop ? "checked" : ""}> preCrop</label>
            </div>
        `)}

        ${_groupHTML("Mask validation", `
            <div class="radiomics-row2">
                <label>Min dimensions:</label>
                <select data-param-path="maskVal.minDims">
                    <option value="1" ${paramsState.maskVal.minDims === 1 ? "selected" : ""}>1 (línea)</option>
                    <option value="2" ${paramsState.maskVal.minDims === 2 ? "selected" : ""}>2 (plano)</option>
                    <option value="3" ${paramsState.maskVal.minDims === 3 ? "selected" : ""}>3 (volumen)</option>
                </select>
            </div>
            <div class="radiomics-row2">
                <label>Min ROI size (vóxeles):</label>
                <input type="number" step="1" min="1" data-param-path="maskVal.minSize" value="${paramsState.maskVal.minSize}">
            </div>
            <div class="radiomics-row2">
                <label><input type="checkbox" data-param-path="maskVal.correctMask" ${paramsState.maskVal.correctMask ? "checked" : ""}> correctMask</label>
            </div>
        `)}

        ${_groupHTML("GLCM / textura", `
            <div class="radiomics-row2">
                <label>distances:</label>
                <input type="text" data-param-path="glcm.distances" value="${_escape(paramsState.glcm.distances)}" placeholder="1, 2, 3">
            </div>
            <div class="radiomics-row2">
                <label>weightingNorm:</label>
                <select data-param-path="glcm.weightingNorm">
                    ${["none","manhattan","euclidean","infinity","no_weighting"]
                        .map(i => `<option value="${i}" ${paramsState.glcm.weightingNorm === i ? "selected" : ""}>${i}</option>`).join("")}
                </select>
            </div>
            <div class="radiomics-row2">
                <label><input type="checkbox" data-param-path="glcm.symmetricalGLCM" ${paramsState.glcm.symmetricalGLCM ? "checked" : ""}> symmetricalGLCM</label>
            </div>
            <div class="radiomics-row2">
                <label>gldm_a:</label>
                <input type="number" step="1" min="0" data-param-path="glcm.gldm_a" value="${paramsState.glcm.gldm_a}">
            </div>
        `)}

        ${_groupHTML("2D extraction", `
            <div class="radiomics-row2">
                <label><input type="checkbox" data-param-path="force2D.enabled" ${paramsState.force2D.enabled ? "checked" : ""}> force2D</label>
            </div>
            <div class="radiomics-row2">
                <label>Out-of-plane axis:</label>
                <select data-param-path="force2D.dimension">
                    <option value="0" ${paramsState.force2D.dimension === 0 ? "selected" : ""}>Z (nativo)</option>
                    <option value="1" ${paramsState.force2D.dimension === 1 ? "selected" : ""}>Y</option>
                    <option value="2" ${paramsState.force2D.dimension === 2 ? "selected" : ""}>X</option>
                </select>
            </div>
        `)}
    `;
}

function _groupHTML(title, inner) {
    return `
        <div class="radiomics-group">
            <div class="radiomics-group-title">${_escape(title)}</div>
            ${inner}
        </div>
    `;
}

function _renderFromClassSection() {
    const container = document.getElementById("radiomics-fromclass");
    if (!container) return;

    const map = _viewerCtx?.segmentMapJsonLoaded;
    const groups = _viewerCtx?.annotationGroups;
    if (!map || !groups) {
        container.innerHTML = "";
        return;
    }

    const options = [];
    for (const [name, info] of map.entries()) {
        const group = groups[info.id - 1];
        // `.size` sólo está en Set; en otras versiones de Cornerstone3D
        // `annotationUIDs` puede ser un Array o un iterable custom. Usamos
        // Array.from(...).length que cubre todos los casos.
        const n = group?.annotationUIDs ? Array.from(group.annotationUIDs).length : 0;
        options.push(`<option value="${_escape(name)}" ${n === 0 ? "disabled" : ""}>${_escape(name)} (${n})</option>`);
    }

    container.innerHTML = `
        <div class="radiomics-group">
            <div class="radiomics-group-title">Desde clase anotada</div>
            <div class="radiomics-fromclass-row">
                <select id="radiomics-fromclass-select">${options.join("")}</select>
                <input type="text" id="radiomics-fromclass-name" placeholder="nombre_roi" maxlength="32">
                <button class="radiomics-primary" data-action="extract_from_class">Extraer ya</button>
            </div>
        </div>
    `;
}

function _renderDrawingSection() {
    const container = document.getElementById("radiomics-drawing");
    if (!container) return;

    if (!drawingState) {
        container.innerHTML = `
            <div class="radiomics-group">
                <div class="radiomics-group-title">Nueva ROI dibujando</div>
                <div class="radiomics-new">
                    <input type="text" id="radiomics-new-name" placeholder="nombre_roi" maxlength="32"
                           autocomplete="off" spellcheck="false">
                    <button data-action="start_drawing" class="radiomics-primary">+ Empezar</button>
                </div>
                <div class="radiomics-new-hint">
                    Usa el tool de polilínea cerrada. Las polilíneas no se guardan en Annotation.
                </div>
            </div>
        `;
        const list = document.getElementById("radiomics-list");
        if (list && !list.innerHTML.trim()) {
            list.innerHTML = `<div class="radiomics-empty">No hay ROIs radiómicas extraídas para este test.</div>`;
        }
        return;
    }

    const { name, polylines, extracting } = drawingState;
    const count = polylines.length;
    container.innerHTML = `
        <div class="radiomics-drawing-active">
            <div class="radiomics-drawing-title">
                Dibujando: <code>${_escape(name)}</code>
            </div>
            <div class="radiomics-drawing-count">
                ${count} polilínea${count === 1 ? "" : "s"} capturada${count === 1 ? "" : "s"}
            </div>
            <div class="radiomics-drawing-actions">
                <button data-action="extract_pending" class="radiomics-primary"
                        ${extracting ? "disabled" : ""}>
                    ${extracting ? "Extrayendo…" : "Extraer"}
                </button>
                <button data-action="cancel_drawing" class="radiomics-secondary"
                        ${extracting ? "disabled" : ""}>
                    Cancelar
                </button>
            </div>
        </div>
    `;
}

function _listHTML(rois) {
    if (!rois.length) {
        return `<div class="radiomics-empty">No hay ROIs radiómicas extraídas para este test.</div>`;
    }
    return rois.map(r => `
        <div class="radiomics-row" data-roi="${_escape(r.name)}">
            <div class="radiomics-row-main">
                <div class="radiomics-row-name">${_escape(r.name)}</div>
                <div class="radiomics-row-meta">
                    ${_fmtVolume(r.roi_volume_mm3)} · ${r.n_voxels_in_roi ?? "?"} vóxeles<br>
                    Extraído ${_fmtDate(r.extracted_at)}${r.extracted_by ? ` por ${_escape(r.extracted_by)}` : ""}<br>
                    <span class="radiomics-hash">params_hash: ${_escape(r.params_hash || "?")}</span>
                </div>
            </div>
            <div class="radiomics-row-actions">
                <button data-action="view" data-roi="${_escape(r.name)}" title="Ver features">👁</button>
                <button data-action="csv"  data-roi="${_escape(r.name)}" title="Descargar CSV">📥</button>
                <button data-action="delete" data-roi="${_escape(r.name)}" title="Borrar" class="radiomics-danger">🗑</button>
            </div>
            <div id="radiomics-detail-${_slug(r.name)}" class="radiomics-detail" data-open="false"></div>
        </div>
    `).join("");
}

function _featuresHTML(payload) {
    const features = payload.features || {};
    const grouped = _groupFeatures(features);

    const sections = FEATURE_FAMILIES.map(fam => {
        const items = grouped[fam.key];
        if (!items || !items.length) return "";
        return `
            <details class="radiomics-family" open>
                <summary>${fam.label} (${items.length})</summary>
                <dl>
                    ${items.map(([name, value]) => `
                        <dt>${_escape(name)}</dt>
                        <dd>${_fmtValue(value)}</dd>
                    `).join("")}
                </dl>
            </details>
        `;
    }).join("");

    const orphan = grouped._orphan;
    const orphanSection = (orphan && orphan.length) ? `
        <details class="radiomics-family">
            <summary>Otras (${orphan.length})</summary>
            <dl>
                ${orphan.map(([name, value]) => `
                    <dt>${_escape(name)}</dt>
                    <dd>${_fmtValue(value)}</dd>
                `).join("")}
            </dl>
        </details>
    ` : "";

    return `
        <div class="radiomics-detail-meta">
            <code>${_escape(payload.test_code || "")}</code> ·
            ${payload.n_polylines ?? "?"} polilíneas ·
            ${Object.keys(features).length} features
        </div>
        ${sections}
        ${orphanSection}
    `;
}

function _groupFeatures(features) {
    const grouped = { _orphan: [] };
    for (const fam of FEATURE_FAMILIES) grouped[fam.key] = [];
    for (const [fullName, value] of Object.entries(features)) {
        const match = fullName.match(/^[^_]+(?:-[^_]+)*_([a-z0-9]+)_(.+)$/i);
        if (match) {
            const fam = match[1].toLowerCase();
            if (grouped[fam] !== undefined) {
                grouped[fam].push([fullName, value]);
                continue;
            }
        }
        grouped._orphan.push([fullName, value]);
    }
    for (const k of Object.keys(grouped)) {
        grouped[k].sort(([a], [b]) => a.localeCompare(b));
    }
    return grouped;
}


// =========================== Formatters ===========================

function _escape(s) {
    if (s === null || s === undefined) return "";
    return String(s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;")
        .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function _slug(s) { return String(s || "").replace(/[^A-Za-z0-9_]/g, "_"); }
function _fmtVolume(mm3) {
    if (mm3 === null || mm3 === undefined) return "? mm³";
    if (mm3 >= 1000) return `${(mm3 / 1000).toFixed(2)} cm³`;
    return `${mm3.toFixed(1)} mm³`;
}
function _fmtDate(isoString) {
    if (!isoString) return "?";
    try {
        const d = new Date(isoString);
        const diff = Date.now() - d.getTime();
        const mins = Math.round(diff / 60000);
        if (mins < 1) return "hace unos segundos";
        if (mins < 60) return `hace ${mins} min`;
        const hours = Math.round(mins / 60);
        if (hours < 48) return `hace ${hours} h`;
        return d.toLocaleString();
    } catch { return isoString; }
}
function _fmtValue(v) {
    if (v === null || v === undefined) return "—";
    if (typeof v === "number") {
        if (Math.abs(v) >= 1000 || (Math.abs(v) > 0 && Math.abs(v) < 0.001)) {
            return v.toExponential(3);
        }
        return v.toFixed(4).replace(/\.?0+$/, "") || "0";
    }
    return _escape(v);
}

// Marcador de uso para el linter.
void _activeRmvCode;